import { Controller } from "@hotwired/stimulus"

// Shared state across all controller instances
let sharedDraggedItem = null
let sharedDraggedChild = null
let sharedSourceTbody = null
let sharedLastInsertPosition = null

// Connects to data-controller="table-row-sortable"
// Handles drag-and-drop sorting of table rows within and between table bodies
export default class extends Controller {
  static values = {
    url: String,
    folderId: Number
  }

  connect() {
    this.boundDragStart = this.dragStart.bind(this)
    this.boundDragOver = this.dragOver.bind(this)
    this.boundDragEnter = this.dragEnter.bind(this)
    this.boundDrop = this.drop.bind(this)
    this.boundDragEnd = this.dragEnd.bind(this)

    // Make all parent rows draggable
    this.parentRows.forEach(row => {
      row.draggable = true
      row.addEventListener('dragstart', this.boundDragStart)
      row.addEventListener('dragend', this.boundDragEnd)
    })

    // Add dragover and drop listeners to the tbody itself
    // This allows dropping from other folders
    this.element.addEventListener('dragover', this.boundDragOver)
    this.element.addEventListener('dragenter', this.boundDragEnter)
    this.element.addEventListener('drop', this.boundDrop)

    // Also add listeners to all rows (including placeholder) for better drop zone coverage
    Array.from(this.element.querySelectorAll('tr')).forEach(row => {
      row.addEventListener('dragover', this.boundDragOver)
      row.addEventListener('drop', this.boundDrop)
    })
  }

  disconnect() {
    this.parentRows.forEach(row => {
      row.draggable = false
      row.removeEventListener('dragstart', this.boundDragStart)
      row.removeEventListener('dragend', this.boundDragEnd)
    })

    this.element.removeEventListener('dragover', this.boundDragOver)
    this.element.removeEventListener('dragenter', this.boundDragEnter)
    this.element.removeEventListener('drop', this.boundDrop)

    Array.from(this.element.querySelectorAll('tr')).forEach(row => {
      row.removeEventListener('dragover', this.boundDragOver)
      row.removeEventListener('drop', this.boundDrop)
    })
  }

  get parentRows() {
    return this.element.querySelectorAll('tr.parent')
  }

  dragStart(event) {
    // Only handle if dragging a parent row (not folder drag operations)
    if (!event.target.classList.contains('parent')) return

    sharedDraggedItem = event.target
    sharedSourceTbody = sharedDraggedItem.closest('tbody')
    sharedDraggedItem.style.opacity = '0.5'
    sharedLastInsertPosition = null

    // Find and store the associated child row
    const classes = Array.from(sharedDraggedItem.classList)
    const mixClass = classes.find(c => c.startsWith('project_folders_recording_mix_'))
    if (mixClass) {
      sharedDraggedChild = sharedSourceTbody.querySelector(`tr.child.${mixClass}`)
    }

    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/html', sharedDraggedItem.innerHTML)
  }

  dragEnter(event) {
    if (!sharedDraggedItem) return
    event.preventDefault()
  }

  dragOver(event) {
    if (!sharedDraggedItem) return

    // Check if we're over THIS controller's tbody element
    const targetTbody = event.target.closest('tbody[data-controller*="table-row-sortable"]')

    // Only handle if this event is for this tbody
    if (!targetTbody || targetTbody !== this.element) return

    event.preventDefault()
    // Don't stop propagation - let other controllers see the event too
    event.dataTransfer.dropEffect = 'move'

    const currentTbody = sharedDraggedItem.closest('tbody')

    // Check if we're over a parent row (not the one being dragged)
    let targetRow = event.target.closest('tr.parent')

    // If we're not over a parent row, check if we're over a child row
    // If so, treat it as being over the parent row that precedes it
    if (!targetRow) {
      const childRow = event.target.closest('tr.child')
      if (childRow && childRow.previousElementSibling && childRow.previousElementSibling.classList.contains('parent')) {
        targetRow = childRow.previousElementSibling
      }
    }

    if (targetRow && targetRow !== sharedDraggedItem && targetRow.parentNode === targetTbody) {
      // Dragging over another parent row in a tbody - insert before or after
      const rect = targetRow.getBoundingClientRect()
      const midpoint = rect.top + rect.height / 2
      let insertBeforeElement = null

      if (event.clientY < midpoint) {
        // Insert before target
        insertBeforeElement = targetRow
      } else {
        // Insert after target (and its child if it has one)
        const targetChild = targetRow.nextElementSibling
        if (targetChild && targetChild.classList.contains('child')) {
          insertBeforeElement = targetChild.nextElementSibling
        } else {
          insertBeforeElement = targetRow.nextElementSibling
        }
      }

      // Only insert if position has changed
      if (insertBeforeElement !== sharedLastInsertPosition) {
        sharedLastInsertPosition = insertBeforeElement
        targetTbody.insertBefore(sharedDraggedItem, insertBeforeElement)
        if (sharedDraggedChild) {
          targetTbody.insertBefore(sharedDraggedChild, insertBeforeElement)
        }
      } else {
      }
    } else {
      // Dragging over placeholder row, empty space, or a different tbody
      const placeholder = targetTbody.querySelector('tr.drop-here')
      const currentDraggedTbody = sharedDraggedItem.closest('tbody')

      if (currentDraggedTbody !== targetTbody) {
        // Moving to a different tbody - insert before placeholder (or at beginning)
        if (placeholder) {
          targetTbody.insertBefore(sharedDraggedItem, placeholder)
          if (sharedDraggedChild) {
            targetTbody.insertBefore(sharedDraggedChild, placeholder)
          }
        } else {
          // No placeholder - check if there are any parent rows
          const firstParent = targetTbody.querySelector('tr.parent')
          if (firstParent) {
            targetTbody.insertBefore(sharedDraggedItem, firstParent)
            if (sharedDraggedChild) {
              targetTbody.insertBefore(sharedDraggedChild, firstParent)
            }
          } else {
            // Completely empty, just append
            targetTbody.appendChild(sharedDraggedItem)
            if (sharedDraggedChild) {
              targetTbody.appendChild(sharedDraggedChild)
            }
          }
        }
      } else {
        // Same tbody, not over a parent row - don't do anything
        // The placeholder is always present, so we shouldn't use it for logic here
      }
    }
  }

  drop(event) {
    if (!sharedDraggedItem) return

    // Only handle drop if it's on this tbody
    const targetTbody = event.target.closest('tbody[data-controller*="table-row-sortable"]')
    if (!targetTbody || targetTbody !== this.element) return

    event.preventDefault()
    return false
  }

  dragEnd(event) {
    if (!sharedDraggedItem) return

    // Stop propagation to prevent folder-level drag from interfering
    event.stopPropagation()

    sharedDraggedItem.style.opacity = ''

    // Ensure child row is positioned correctly after parent
    const currentTbody = sharedDraggedItem.closest('tbody')
    if (sharedDraggedChild) {
      currentTbody.insertBefore(sharedDraggedChild, sharedDraggedItem.nextElementSibling)
    }

    // Update empty class on both source and destination tbodies
    this.updateEmptyState(sharedSourceTbody)
    if (currentTbody !== sharedSourceTbody) {
      this.updateEmptyState(currentTbody)

      // If moved to a different folder, we need to update that folder's server
      const targetController = this.application.getControllerForElementAndIdentifier(
        currentTbody,
        'table-row-sortable'
      )
      if (targetController) {
        targetController.updateServer()
      }
    }

    // Send update to server for the source folder (or current if same)
    this.updateServer()

    sharedDraggedItem = null
    sharedDraggedChild = null
    sharedSourceTbody = null
    sharedLastInsertPosition = null
  }

  updateEmptyState(tbody) {
    if (!tbody) return

    const hasParentRows = tbody.querySelectorAll('tr.parent').length > 0
    if (hasParentRows) {
      tbody.classList.remove('empty')
    } else {
      tbody.classList.add('empty')
    }
  }

  updateServer() {
    const currentTbody = this.element
    const folderId = this.folderIdValue
    const url = `/my/folders/${folderId}/tracks/sort`
    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    // Serialize the order of parent rows in this tbody
    const formData = new FormData()
    currentTbody.querySelectorAll('tr.parent').forEach(row => {
      const match = row.id.match(/project_folders_recording_mix_parent_(\d+)/)
      if (match) {
        formData.append('project_folders_recording_mix_parent[]', match[1])
      }
    })

    fetch(url, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken
      },
      body: formData,
      credentials: 'same-origin'
    }).catch(error => {
      console.error('Sort update failed:', error)
      alert('Failed to update sort order')
    })
  }
}
